<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { NModal, NForm, NFormItem, NInput, NSwitch, NSelect, NButton } from 'naive-ui'
import { useCreateVehicle, useUpdateVehicle, type Vehicle } from '@/api/vehicles'
import type { TenantDevice } from '@/api/devices'

interface Props {
  show: boolean
  mode: 'create' | 'edit'
  vehicle?: Vehicle | null
  freeDevices?: TenantDevice[]
}
const props = defineProps<Props>()
const emit = defineEmits<{
  'update:show': [boolean]
  created: [Vehicle]
  updated: [Vehicle]
}>()

const name = ref('')
const isActive = ref(true)
const deviceId = ref<string | null>(null)
const submitError = ref<string | null>(null)

// Re-seed form state only on rising edge of `show` — иначе фоновый refetch
// `props.vehicle` (vue-query на VehicleDetailPage) затирает ввод пользователя.
watch(
  () => props.show,
  (show) => {
    if (show) {
      name.value = props.vehicle?.name ?? ''
      isActive.value = props.vehicle?.is_active ?? true
      deviceId.value = null
      submitError.value = null
    }
  },
  { immediate: true },
)

const createMut = useCreateVehicle()
const updateMut = useUpdateVehicle()

const deviceOptions = computed(() =>
  (props.freeDevices ?? []).map((d) => ({
    label: d.label ? `${d.serial} — ${d.label}` : d.serial,
    value: d.id,
  })),
)

const trimmedName = computed(() => name.value.trim())
const canSubmit = computed(() => trimmedName.value.length > 0)
const submitting = computed(() =>
  props.mode === 'create' ? createMut.isPending.value : updateMut.isPending.value,
)

async function submit() {
  submitError.value = null
  if (!canSubmit.value) return
  try {
    if (props.mode === 'create') {
      const created = await createMut.mutateAsync({
        name: trimmedName.value,
        device_id: deviceId.value ?? undefined,
      })
      emit('created', created)
      emit('update:show', false)
    } else {
      if (!props.vehicle) return
      const updated = await updateMut.mutateAsync({
        id: props.vehicle.id,
        body: { name: trimmedName.value, is_active: isActive.value },
      })
      emit('updated', updated)
      emit('update:show', false)
    }
  } catch (e: unknown) {
    submitError.value = (e as Error)?.message ?? 'Ошибка сохранения'
  }
}

function cancel() {
  emit('update:show', false)
}
</script>

<template>
  <n-modal
    :show="show"
    preset="card"
    :title="mode === 'create' ? 'Добавить машину' : 'Редактировать машину'"
    style="width: 480px; max-width: 95vw"
    @update:show="$emit('update:show', $event)"
  >
    <n-form>
      <n-form-item label="Название" :feedback="submitError ?? ''">
        <n-input v-model:value="name" placeholder="Например, Volvo XC90" />
      </n-form-item>
      <n-form-item v-if="mode === 'create'" label="Девайс (опционально)">
        <n-select
          v-model:value="deviceId"
          :options="deviceOptions"
          clearable
          placeholder="Привязать позже"
        />
      </n-form-item>
      <n-form-item v-if="mode === 'edit'" label="Активна">
        <n-switch v-model:value="isActive" />
      </n-form-item>
    </n-form>
    <template #footer>
      <n-button data-action="cancel" @click="cancel">Отмена</n-button>
      <n-button
        type="primary"
        data-action="submit"
        :disabled="!canSubmit || submitting"
        :loading="submitting"
        style="margin-left: 8px"
        @click="submit"
      >
        Сохранить
      </n-button>
    </template>
  </n-modal>
</template>
